import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type {
  GraphQLCommitment,
  GraphQLSupporterGiftLimits,
  GraphQLSupporterGiftTypeLimits,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  isItemGift,
  isItemGiftCenter,
} from '@sola/shared-utils/cart/cart-item-type-guards.ts';
import {
  isBirthdayGift,
  isChildGift,
} from '@sola/shared-utils/my/gifts-type-guards.ts';

import type { GiftOption } from '../../types/gifts.ts';
import {
  BadgeLabel,
  ButtonAction,
  GiftTooltip,
  ProgressLabelKey,
} from '../../types/gifts.ts';

export type CartContext = {
  giftType: GiftTypeEnum;
  option: GiftOption;
  cartItems: CartItem[];
  globalId: GraphQLCommitment['globalId'];
  giftTypes: GraphQLSupporterGiftTypeLimits;
  defaults: GraphQLSupporterGiftLimits['defaults'];
};

type CartRule = {
  match: (ctx: CartContext) => boolean;
  resolve: (ctx: CartContext) => GiftOption;
};

const findChildCartItem = (
  cartItems: CartItem[],
  giftType: GiftTypeEnum,
  globalId: string,
): CartItem | undefined =>
  cartItems.find(
    (item) =>
      isItemGift(item) &&
      item.gift.giftType === giftType &&
      'globalId' in item.gift &&
      item.gift.globalId === globalId,
  );

const hasCartItemForChild =
  (giftType: GiftTypeEnum) =>
  (cartItems: CartItem[], globalId: string): boolean =>
    findChildCartItem(cartItems, giftType, globalId) !== undefined;

const hasBirthdayInCart = hasCartItemForChild(GiftTypeEnum.Birthday);
const hasChildGiftInCart = hasCartItemForChild(GiftTypeEnum.Child);

const applyCartOverride = (
  option: GiftOption,
  cartItem: CartItem,
): GiftOption => ({
  ...option,
  cartItem: {
    cartId: cartItem.id,
    amount: cartItem.amount,
    frequency: cartItem.commitmentFrequency,
  },
  buttonAction: ButtonAction.Edit,
  progressLabel: null,
  tooltip: null,
  badge: null,
});

/** Cart rules for child gift types, evaluated in priority order (first match wins) */
const childCartRules: CartRule[] = [
  /** 1. This exact gift is in the cart */
  {
    match: ({ giftType, cartItems, globalId }) =>
      findChildCartItem(cartItems, giftType, globalId) !== undefined,
    resolve: ({ giftType, option, cartItems, globalId }) =>
      applyCartOverride(
        option,
        findChildCartItem(cartItems, giftType, globalId)!,
      ),
  },

  /** 2. Birthday card: child gift in cart would hit combined cap, ineligible */
  {
    match: ({ giftType, option, cartItems, globalId, giftTypes, defaults }) => {
      if (!isBirthdayGift(giftType) || !option.canGive) {
        return false;
      }
      if (!hasChildGiftInCart(cartItems, globalId)) {
        return false;
      }

      const childLimits = giftTypes[GiftTypeEnum.Child];
      const childDefaults = defaults[GiftTypeEnum.Child];
      if (!childLimits || !childDefaults) {
        return false;
      }

      const childGiftsGiven =
        childDefaults.remainingQuantity - childLimits.remainingQuantity;
      return childGiftsGiven + 1 >= childDefaults.remainingQuantity;
    },
    resolve: ({ option, giftTypes, defaults }) => {
      const childLimits = giftTypes[GiftTypeEnum.Child]!;
      const childDefaults = defaults[GiftTypeEnum.Child]!;
      const childGiftsGiven =
        childDefaults.remainingQuantity - childLimits.remainingQuantity;
      const childMax = childDefaults.remainingQuantity;
      return {
        ...option,
        canGive: false,
        progressLabel: {
          given: childGiftsGiven + 1,
          max: childMax,
          labelKey: ProgressLabelKey.CartConflict,
        },
        tooltip: { key: GiftTooltip.BirthdayGiftIneligible, count: childMax },
        buttonAction: ButtonAction.SetupRecurring,
      };
    },
  },

  /** 3. Child card: birthday in cart counts against child gift progress */
  {
    match: ({ giftType, cartItems, globalId, giftTypes, defaults }) => {
      if (!isChildGift(giftType) || !hasBirthdayInCart(cartItems, globalId)) {
        return false;
      }

      const childLimits = giftTypes[GiftTypeEnum.Child];
      const childDefaults = defaults[GiftTypeEnum.Child];
      if (!childLimits || !childDefaults) {
        return false;
      }

      return true;
    },
    resolve: ({ option, giftTypes, defaults }) => {
      const childLimits = giftTypes[GiftTypeEnum.Child]!;
      const childDefaults = defaults[GiftTypeEnum.Child]!;
      const childGiftsGiven =
        childDefaults.remainingQuantity - childLimits.remainingQuantity;
      const childMax = childDefaults.remainingQuantity;
      const given = childGiftsGiven + 1;
      const isMaxed = given >= childMax;

      return {
        ...option,
        canGive: !isMaxed,
        buttonAction: isMaxed ? null : ButtonAction.GiveAnother,
        progressLabel: {
          given,
          max: childMax,
          labelKey: ProgressLabelKey.GiftsPlural,
        },
        tooltip: {
          key: GiftTooltip.BirthdayGiftNotRecurring,
          count: isMaxed ? childMax : undefined,
        },
      };
    },
  },
];

export const applyChildCartAwareness = (
  giftType: GiftTypeEnum,
  option: GiftOption | undefined,
  cartItems: CartItem[],
  globalId: GraphQLCommitment['globalId'],
  giftTypes: GraphQLSupporterGiftTypeLimits,
  defaults: GraphQLSupporterGiftLimits['defaults'],
): GiftOption | undefined => {
  if (!option) {
    return undefined;
  }

  if (
    giftType === GiftTypeEnum.Final &&
    option.tooltip?.key === GiftTooltip.FinalGiftAlreadyGiven
  ) {
    return option;
  }

  const cartContext: CartContext = {
    giftType,
    option,
    cartItems,
    globalId,
    giftTypes,
    defaults,
  };

  const matched = childCartRules.find((rule) => rule.match(cartContext));
  return matched ? matched.resolve(cartContext) : option;
};

/** NOTE: Transforms a gift option into the "Same Center" peer presentation state. */
export const toSameCenterPeerOption = (option: GiftOption): GiftOption => ({
  ...option,
  buttonAction: null,
  progressLabel: null,
  cartItem: null,
  tooltip: { key: GiftTooltip.SameCenterNotification },
  badge: { label: BadgeLabel.SameCenter, color: 'brand', icon: 'church' },
});

/**
 * NOTE: Resolves center cart state:
 * - if this child is the one associated with the center gift in cart, show 'edit' mode
 * - if a peer child's gift at same center is in the cart, show the "Same Center" badge
 * - otherwise, display the default gift option view.
 */
export const applyCenterCartAwareness = (
  option: GiftOption,
  cartItems: CartItem[],
  centerId: string,
  globalId: string,
  isAssociatedWithCenterGift: boolean, // Used to designate which gift card will show the "gift in cart" view, and which ones will potentially show "same center" badges.
): GiftOption => {
  const associatedCartItem = cartItems.find(
    (item) =>
      isItemGiftCenter(item) &&
      item.gift.centerId === centerId &&
      (item.child?.globalId === globalId ||
        (!item.child && isAssociatedWithCenterGift)),
  );
  if (associatedCartItem) {
    return applyCartOverride(option, associatedCartItem);
  }

  const sameCenterInCart = cartItems.some(
    (item) =>
      isItemGiftCenter(item) &&
      item.gift.centerId === centerId &&
      (item.child?.globalId !== globalId ||
        (!item.child && !isAssociatedWithCenterGift)),
  );

  return sameCenterInCart ? toSameCenterPeerOption(option) : option;
};
